import {
  DEFAULT_ASPECT_ORBS,
  MAJOR_ASPECTS,
  type AspectHit,
  type AspectOrbConfig,
  type ChartPointId,
  type WesternAspectType,
} from '@astrally/shared';

export { MAJOR_ASPECTS };
import { separation } from '../math/angles.js';

export const ASPECT_ANGLES: Record<WesternAspectType, number> = {
  conjunction: 0,
  opposition: 180,
  trine: 120,
  square: 90,
  sextile: 60,
  quincunx: 150,
  'semi-sextile': 30,
  'semi-square': 45,
  sesquiquadrate: 135,
};

export interface ChartPoint {
  id: ChartPointId;
  lon: number;
}

function orbAllowed(
  type: WesternAspectType,
  a: ChartPointId,
  b: ChartPointId,
  orbs: AspectOrbConfig,
): number {
  const base = orbs[type];
  const luminary = a === 'sun' || a === 'moon' || b === 'sun' || b === 'moon';
  return luminary ? base + orbs.luminaryBonus : base;
}

/**
 * Find the matching aspect (if any) between two longitudes.
 * Pass `types` to restrict the search — e.g. MAJOR_ASPECTS for a clean wheel.
 */
export function matchAspect(
  a: ChartPoint,
  b: ChartPoint,
  orbs: AspectOrbConfig = DEFAULT_ASPECT_ORBS,
  types: WesternAspectType[] = Object.keys(ASPECT_ANGLES) as WesternAspectType[],
): AspectHit | null {
  const sep = separation(a.lon, b.lon);
  let best: AspectHit | null = null;
  for (const type of types) {
    const deviation = Math.abs(sep - ASPECT_ANGLES[type]);
    if (deviation <= orbAllowed(type, a.id, b.id, orbs)) {
      if (!best || deviation < best.orb) {
        best = { a: a.id, b: b.id, type, orb: Number(deviation.toFixed(2)) };
      }
    }
  }
  return best;
}

/** All aspects among a set of chart points (each unordered pair once). */
export function computeAspects(
  points: ChartPoint[],
  orbs: AspectOrbConfig = DEFAULT_ASPECT_ORBS,
  types?: WesternAspectType[],
): AspectHit[] {
  const hits: AspectHit[] = [];
  for (let i = 0; i < points.length; i++) {
    for (let j = i + 1; j < points.length; j++) {
      const hit = matchAspect(points[i], points[j], orbs, types);
      if (hit) hits.push(hit);
    }
  }
  return hits;
}

/** Aspects from one set of points to another (used for transits & synastry). */
export function computeCrossAspects(
  from: ChartPoint[],
  to: ChartPoint[],
  orbs: AspectOrbConfig = DEFAULT_ASPECT_ORBS,
  types?: WesternAspectType[],
): AspectHit[] {
  const hits: AspectHit[] = [];
  for (const f of from) {
    for (const t of to) {
      const hit = matchAspect(f, t, orbs, types);
      if (hit) hits.push(hit);
    }
  }
  return hits;
}

/**
 * Part of Fortune (Pars Fortunae) — the classical Arabic part, and the one
 * Western chart point most often missed by software that only plots bodies.
 *
 * Day births:   Asc + Moon − Sun
 * Night births: Asc + Sun − Moon
 * "Night" means the Sun is above the horizon's... below it: the Sun sits in
 * houses 7–12, i.e. between the Ascendant and the Descendant going forward.
 */
export function partOfFortune(
  ascendant: number,
  sunLon: number,
  moonLon: number,
): { lon: number; sect: 'day' | 'night' } {
  // Sun above the horizon (houses 7-12) → day chart.
  const sunFromAsc = ((sunLon - ascendant) % 360 + 360) % 360;
  const isDay = sunFromAsc >= 180;
  const lon = isDay
    ? ((ascendant + moonLon - sunLon) % 360 + 360) % 360
    : ((ascendant + sunLon - moonLon) % 360 + 360) % 360;
  return { lon, sect: isDay ? 'day' : 'night' };
}
